import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const COMMISSION_RATE = 1.2;
const LEAD_ONLY_PRICE = 100;

export default function SuppliersAdmin({ userRole, currentUserId, isDarkMode }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [activeTab, setActiveTab] = useState("suppliers");
  const [costsSummary, setCostsSummary] = useState([]);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [monthBonus, setMonthBonus] = useState(0);
  const [monthBonusNote, setMonthBonusNote] = useState("");
  const [savingBonus, setSavingBonus] = useState(false);
  const [bonusMsg, setBonusMsg] = useState("");

  const bg = isDarkMode ? "#1e293b" : "#f8fafc";
  const card = isDarkMode ? "#0f172a" : "#ffffff";
  const text = isDarkMode ? "#f1f5f9" : "#1e293b";
  const subtext = isDarkMode ? "#94a3b8" : "#64748b";
  const border = isDarkMode ? "#334155" : "#e2e8f0";

  const s = {
    container: { padding: "24px", maxWidth: 1240, margin: "0 auto", color: text },
    card: { background: card, borderRadius: 12, border: `1px solid ${border}`, marginBottom: 20, overflow: "hidden" },
    cardHeader: { padding: "16px 20px", borderBottom: `1px solid ${border}`, fontWeight: 700, fontSize: 16 },
    cardBody: { padding: "16px 20px" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
    th: { textAlign: "left", padding: "10px 12px", borderBottom: `1px solid ${border}`, fontWeight: 600, color: subtext, background: bg },
    td: { padding: "10px 12px", borderBottom: `1px solid ${border}` },
    btn: { padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, border: "none" },
    btnPrimary: { background: "#2563eb", color: "#fff" },
    btnSecondary: { background: bg, color: text, border: `1px solid ${border}` },
    input: {
      border: `1px solid ${border}`,
      borderRadius: 8,
      padding: "8px 10px",
      background: isDarkMode ? "#0b1220" : "#fff",
      color: text,
      fontSize: 13,
    },
    tag: (status) => ({
      fontSize: 11,
      padding: "2px 8px",
      borderRadius: 99,
      fontWeight: 700,
      background: status === "approved" ? "#dcfce7" : status === "pending" ? "#fef3c7" : "#fee2e2",
      color: status === "approved" ? "#16a34a" : status === "pending" ? "#b45309" : "#dc2626",
    }),
  };

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/admin/suppliers?userId=${currentUserId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba serveru");
      const list = Array.isArray(data) ? data : Array.isArray(data.suppliers) ? data.suppliers : [];
      setSuppliers(list);
    } catch (e) {
      setError(e.message || "Nepodařilo se načíst dodavatele.");
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  const fetchCostsSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/suppliers/costs-summary?userId=${currentUserId}`);
      const data = await res.json();
      if (res.ok && data.summary) setCostsSummary(data.summary);
    } catch {
      // non-fatal
    }
  }, [currentUserId]);

  useEffect(() => {
    if (userRole === "admin") {
      fetchSuppliers();
      fetchCostsSummary();
    }
  }, [fetchSuppliers, fetchCostsSummary, userRole]);

  const openLeads = async (supplier) => {
    setSelectedSupplier(supplier);
    setActiveTab("leads");
    setLeads([]);
    setLoadingLeads(true);
    setBonusMsg("");
    setInvoiceMsg("");
    try {
      const res = await fetch(`${API_URL}/admin/suppliers/${supplier._id}/leads?month=${selectedMonth}&userId=${currentUserId}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : Array.isArray(data.companies) ? data.companies : [];
      setLeads(list);
      setMonthBonus(Number(data?.monthBonus?.amount || 0));
      setMonthBonusNote(data?.monthBonus?.note || "");
    } catch {
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    if (activeTab === "leads" && selectedSupplier) {
      openLeads(selectedSupplier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  const saveMonthBonus = async () => {
    if (!selectedSupplier) return;
    setSavingBonus(true);
    setBonusMsg("");
    try {
      const res = await fetch(`${API_URL}/admin/suppliers/${selectedSupplier._id}/month-bonus?userId=${currentUserId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month: selectedMonth, amount: Number(monthBonus || 0), note: monthBonusNote }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba při ukládání bonusu");
      setMonthBonus(Number(data?.monthBonus?.amount || 0));
      setMonthBonusNote(data?.monthBonus?.note || "");
      setBonusMsg("Bonus uložen");
      setTimeout(() => setBonusMsg(""), 3000);
    } catch (e) {
      setBonusMsg(e.message || "Chyba při ukládání bonusu");
    } finally {
      setSavingBonus(false);
    }
  };

  const setLeadPayoutStatus = async (lead, status) => {
    if (!selectedSupplier || !lead?._id) return;
    const nextStatus = lead.payoutStatus === status ? null : status;

    const originalStatus = lead.payoutStatus || null;
    const originalMarkedAt = lead.payoutMarkedAt || null;
    const originalMarkedBy = lead.payoutMarkedByName || "";

    setLeads(prev => prev.map(item => {
      if (item._id !== lead._id) return item;
      return {
        ...item,
        payoutStatus: nextStatus,
        payoutMarkedAt: nextStatus ? new Date().toISOString() : null,
        payoutMarkedByName: nextStatus ? "Ukládám..." : "",
      };
    }));

    try {
      const res = await fetch(`${API_URL}/admin/suppliers/${selectedSupplier._id}/lead-payout?userId=${currentUserId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId: lead._id,
          month: selectedMonth,
          payoutStatus: nextStatus,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba při ukládání stavu");

      const mark = data.mark || null;
      setLeads(prev => prev.map(item => {
        if (item._id !== lead._id) return item;
        return {
          ...item,
          payoutStatus: mark?.payoutStatus || null,
          payoutMarkedAt: mark?.markedAt || null,
          payoutMarkedByName: mark?.markedByName || "",
        };
      }));
    } catch {
      setLeads(prev => prev.map(item => {
        if (item._id !== lead._id) return item;
        return {
          ...item,
          payoutStatus: originalStatus,
          payoutMarkedAt: originalMarkedAt,
          payoutMarkedByName: originalMarkedBy,
        };
      }));
    }
  };

  const sendInvoiceByEmail = async () => {
    if (!selectedSupplier) return;
    setSendingInvoice(true);
    setInvoiceMsg("");
    try {
      const res = await fetch(`${API_URL}/admin/suppliers/${selectedSupplier._id}/send-invoice?month=${selectedMonth}&userId=${currentUserId}`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba serveru");
      setInvoiceMsg(data.message || "Faktura odeslána.");
    } catch (e) {
      setInvoiceMsg("Chyba: " + (e.message || "Nepodařilo se odeslat."));
    } finally {
      setSendingInvoice(false);
    }
  };

  const getBmsl = (lead) => Number(lead.bmsl || lead.BMSL || lead.mesicniNajem || 0);
  const getName = (lead) => lead.name || lead.nazevFirmy || lead.companyName || "–";
  const isMarked = (lead) => lead.payoutStatus === "complete" || lead.payoutStatus === "lead_only";
  const getLeadInvoiceAmount = (lead) => {
    if (lead.payoutStatus === "lead_only") return LEAD_ONLY_PRICE;
    if (lead.payoutStatus === "complete") return getBmsl(lead) * COMMISSION_RATE;
    return 0;
  };

  const markedLeads = leads.filter(isMarked);
  const invoiceBase = markedLeads.reduce((sum, lead) => sum + getLeadInvoiceAmount(lead), 0);
  const invoiceTotal = Math.round((invoiceBase + Number(monthBonus || 0)) * 100) / 100;

  const monthLabel = (() => {
    const [y, m] = selectedMonth.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("cs-CZ", { month: "long", year: "numeric" });
  })();

  const openCompanyInNewTab = (companyId) => {
    if (!companyId) return;
    const href = `${window.location.origin}${window.location.pathname}#/companies/${companyId}`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const printInvoice = () => {
    if (!selectedSupplier) return;

    const invoiceNumber = `SB-${selectedSupplier.ico}-${selectedMonth.replace("-", "")}`;
    const invoiceDate = new Date().toLocaleDateString("cs-CZ");
    const [smY, smM] = selectedMonth.split("-");
    const duzp = new Date(Number(smY), Number(smM), 0).toLocaleDateString("cs-CZ");

    const rowsHtml = markedLeads.map((lead, idx) => {
      const payoutStatusLabel = lead.payoutStatus === "complete" ? "Proplaceno komplet" : "Proplaceno pouze lead";
      const markedAt = lead.payoutMarkedAt ? new Date(lead.payoutMarkedAt).toLocaleDateString("cs-CZ") : "–";
      const rowAmount = Math.round(getLeadInvoiceAmount(lead) * 100) / 100;
      return `
        <tr>
          <td>${idx + 1}</td>
          <td>${getName(lead)}</td>
          <td>${lead.ico || "–"}</td>
          <td>${payoutStatusLabel}</td>
          <td>${markedAt}</td>
          <td>${Number(getBmsl(lead)).toLocaleString("cs-CZ")} Kč</td>
          <td>${rowAmount.toLocaleString("cs-CZ")} Kč</td>
          <td>${lead.closedDate ? new Date(lead.closedDate).toLocaleDateString("cs-CZ") : "–"}</td>
        </tr>`;
    }).join("");

    const invoiceHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Self-billing faktura ${invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 18px; color: #111827; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
            .title { font-size: 20px; font-weight: 800; margin: 0; }
            .sub { color: #6b7280; font-size: 12px; margin-top: 4px; }
            .box-wrap { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 12px 0; }
            .box { border: 1px solid #d1d5db; border-radius: 8px; padding: 10px; }
            .box h3 { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; color: #374151; }
            .box p { margin: 4px 0; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 8px; font-size: 11px; text-align: left; }
            th { background: #f3f4f6; }
            .totals { margin-top: 14px; margin-left: auto; width: 360px; }
            .totals .line { display: flex; justify-content: space-between; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 12px; }
            .totals .final { font-size: 16px; font-weight: 800; color: #065f46; }
            .note { margin-top: 12px; font-size: 11px; color: #4b5563; border-top: 1px solid #e5e7eb; padding-top: 8px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <p class="title">Self-billing faktura ${invoiceNumber}</p>
              <p class="sub">Fakturovaný měsíc: ${monthLabel}</p>
            </div>
            <div style="text-align:right; font-size:12px;">
              <div><strong>Datum vystavení:</strong> ${invoiceDate}</div>              ${selectedSupplier.vatPayer ? `<div><strong>DUZP:</strong> ${duzp}</div>` : ''}            </div>
          </div>

          <div class="box-wrap">
            <div class="box">
              <h3>Dodavatel</h3>
              <p><strong>${selectedSupplier.companyName}</strong></p>
              <p>Adresa: ${selectedSupplier.address || "–"}</p>
              <p>IČO: ${selectedSupplier.ico || "–"}</p>
              <p>DIČ: ${selectedSupplier.dic || "–"}</p>
              <p>Bankovní účet: ${selectedSupplier.bankAccount || "–"}</p>
              <p>${selectedSupplier.vatPayer ? "Plátce DPH" : "Neplátce DPH"}</p>
            </div>
            <div class="box">
              <h3>Odběratel</h3>
              <p><strong>Vyjednáme s.r.o.</strong></p>
              <p>Příčná 1892/4, Nové Město (Praha 1)</p>
              <p>110 00 Praha</p>
              <p>IČO: 21377596</p>
              <p>DIČ: CZ21377596</p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Firma zákazníka</th>
                <th>IČO</th>
                <th>Stav proplacení</th>
                <th>Datum označení</th>
                <th>BMSL</th>
                <th>Cena</th>
                <th>Datum uzavření</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || '<tr><td colspan="7" style="text-align:center;color:#6b7280">Žádné označené firmy pro tento měsíc.</td></tr>'}
            </tbody>
          </table>

          <div class="totals">
            <div class="line"><span>Základ fakturace</span><strong>${Math.round(invoiceBase).toLocaleString("cs-CZ")} Kč</strong></div>
            <div class="line"><span>Bonus za měsíc</span><strong>${Number(monthBonus || 0).toLocaleString("cs-CZ")} Kč</strong></div>
            ${monthBonusNote ? `<div class="line"><span>Poznámka k bonusu</span><strong>${monthBonusNote}</strong></div>` : ""}
            <div class="line final"><span>Celkem k proplacení</span><span>${invoiceTotal.toLocaleString("cs-CZ")} Kč</span></div>
          </div>

          <div class="note">
            Faktura zahrnuje pouze firmy označené pro vybraný měsíc. Pro "Proplaceno pouze lead" je cena pevně ${LEAD_ONLY_PRICE} Kč, pro "Proplaceno komplet" je cena 120 % z BMSL.
          </div>
        </body>
      </html>
    `;

    const win = window.open("", "_blank");
    win.document.write(invoiceHtml);
    win.document.close();
    win.print();
  };

  if (userRole !== "admin") return null;

  return (
    <div style={{ background: bg, minHeight: "100vh" }}>
      <div style={s.container}>
        <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
          <button
            style={{ ...s.btn, ...(activeTab === "suppliers" ? s.btnPrimary : s.btnSecondary) }}
            onClick={() => setActiveTab("suppliers")}
          >
            Dodavatelé
          </button>
          {selectedSupplier && (
            <button
              style={{ ...s.btn, ...(activeTab === "leads" ? s.btnPrimary : s.btnSecondary) }}
              onClick={() => setActiveTab("leads")}
            >
              Leady - {selectedSupplier.companyName}
            </button>
          )}
        </div>

        {activeTab === "suppliers" && (
          <div style={s.card}>
            <div style={{ ...s.cardHeader, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Dodavatelé ({suppliers.length})</span>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={fetchSuppliers} disabled={loading}>
                {loading ? "Načítám..." : "Obnovit"}
              </button>
            </div>
            <div style={s.cardBody}>
              {error && <div style={{ color: "#dc2626", marginBottom: 12 }}>{error}</div>}

              {costsSummary.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: subtext }}>Přehled celkových nákladů</div>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Dodavatel</th>
                        <th style={s.th}>Šarže</th>
                        <th style={s.th}>Uzavřených</th>
                        <th style={s.th}>Celk. BMSL (Kč)</th>
                        <th style={s.th}>Provize 120 % BMSL (Kč)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costsSummary.map(row => (
                        <tr key={row.supplierId}>
                          <td style={s.td}>{row.companyName}</td>
                          <td style={s.td}>{row.batchName}</td>
                          <td style={s.td}>{row.closedCount}</td>
                          <td style={s.td}>{row.totalBmsl.toLocaleString("cs-CZ")}</td>
                          <td style={s.td}>{row.commission.toLocaleString("cs-CZ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {suppliers.length === 0 && !loading && <p style={{ color: subtext }}>Žádní dodavatelé.</p>}
              {suppliers.length > 0 && (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Firma</th>
                      <th style={s.th}>IČO</th>
                      <th style={s.th}>Email</th>
                      <th style={s.th}>Šarže</th>
                      <th style={s.th}>Stav</th>
                      <th style={s.th}>Akce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map(supplier => (
                      <tr key={supplier._id}>
                        <td style={s.td}>{supplier.companyName}</td>
                        <td style={s.td}>{supplier.ico}</td>
                        <td style={s.td}>{supplier.email}</td>
                        <td style={s.td}>{supplier.batchName || "-"}</td>
                        <td style={s.td}><span style={s.tag(supplier.status)}>{supplier.status === "approved" ? "Schváleno" : supplier.status === "pending" ? "Čeká" : "Zamítnuto"}</span></td>
                        <td style={s.td}>
                          {supplier.status === "approved" && (
                            <button style={{ ...s.btn, ...s.btnSecondary, fontSize: 12 }} onClick={() => openLeads(supplier)}>
                              Leady
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === "leads" && selectedSupplier && (
          <>
            <div style={s.card}>
              <div style={{ ...s.cardHeader, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span>Generátor faktury - {selectedSupplier.companyName}</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={s.input} />
                  <button style={{ ...s.btn, ...s.btnSecondary, fontSize: 12 }} onClick={printInvoice}>🖨 Tisk</button>
                  <button
                    style={{ ...s.btn, ...s.btnPrimary, fontSize: 12, background: sendingInvoice ? "#94a3b8" : "#16a34a" }}
                    onClick={sendInvoiceByEmail}
                    disabled={sendingInvoice}
                  >
                    {sendingInvoice ? "Odesílám..." : "📧 Odeslat emailem"}
                  </button>
                </div>
              </div>

              {invoiceMsg && (
                <div style={{
                  padding: "10px 20px",
                  background: invoiceMsg.startsWith("Chyba") ? "#fee2e2" : "#dcfce7",
                  color: invoiceMsg.startsWith("Chyba") ? "#dc2626" : "#16a34a",
                  fontSize: 13,
                  borderBottom: `1px solid ${border}`,
                }}>
                  {invoiceMsg}
                </div>
              )}

              <div style={s.cardBody}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginBottom: 16 }}>
                  <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, color: subtext }}>Fakturovaný měsíc</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{monthLabel}</div>
                  </div>
                  <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, color: subtext }}>Označené firmy</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{markedLeads.length}</div>
                  </div>
                  <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 10 }}>
                    <div style={{ fontSize: 12, color: subtext }}>Základ fakturace</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{Math.round(invoiceBase).toLocaleString("cs-CZ")} Kč</div>
                  </div>
                  <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 10, background: isDarkMode ? "#052e16" : "#ecfdf5" }}>
                    <div style={{ fontSize: 12, color: subtext }}>Celkem k proplacení</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#16a34a" }}>{invoiceTotal.toLocaleString("cs-CZ")} Kč</div>
                  </div>
                </div>

                <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>Měsíční bonus (admin)</div>
                  <div style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 8, alignItems: "center" }}>
                    <input
                      type="number"
                      value={monthBonus}
                      onChange={(e) => setMonthBonus(e.target.value)}
                      style={s.input}
                      placeholder="0"
                    />
                    <input
                      type="text"
                      value={monthBonusNote}
                      onChange={(e) => setMonthBonusNote(e.target.value)}
                      style={s.input}
                      placeholder="Poznámka k bonusu (volitelné)"
                    />
                    <button onClick={saveMonthBonus} style={{ ...s.btn, ...s.btnPrimary }} disabled={savingBonus}>
                      {savingBonus ? "Ukládám..." : "Uložit bonus"}
                    </button>
                  </div>
                  {bonusMsg && <div style={{ fontSize: 12, marginTop: 8, color: bonusMsg.includes("Chyba") ? "#dc2626" : "#16a34a" }}>{bonusMsg}</div>}
                </div>
              </div>
            </div>

            <div style={s.card}>
              <div style={s.cardHeader}>Leady dodavatele ({leads.length})</div>
              <div style={s.cardBody}>
                {loadingLeads && <p style={{ color: subtext }}>Načítám...</p>}
                {!loadingLeads && leads.length === 0 && <p style={{ color: subtext }}>Žádné leady.</p>}
                {!loadingLeads && leads.length > 0 && (
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Firma</th>
                        <th style={s.th}>IČO</th>
                        <th style={s.th}>Vlastník</th>
                        <th style={s.th}>FC (%)</th>
                        <th style={s.th}>BMSL (Kč)</th>
                        <th style={s.th}>Datum uzavření</th>
                        <th style={s.th}>Stav proplacení</th>
                        <th style={s.th}>Cena k proplacení</th>
                        <th style={s.th}>Datum označení</th>
                        <th style={s.th}>Akce</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map(lead => {
                        const rowBg = lead.payoutStatus === "complete"
                          ? (isDarkMode ? "#14532d55" : "#dcfce7")
                          : lead.payoutStatus === "lead_only"
                            ? (isDarkMode ? "#78350f55" : "#fef3c7")
                            : "transparent";

                        return (
                          <tr key={lead._id} style={{ background: rowBg }}>
                            <td style={s.td}>
                              <button
                                onClick={() => openCompanyInNewTab(lead._id)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: isDarkMode ? "#93c5fd" : "#1d4ed8",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  padding: 0,
                                  fontWeight: 700,
                                }}
                                title="Otevřít detail firmy v nové kartě"
                              >
                                {getName(lead)}
                              </button>
                            </td>
                            <td style={s.td}>{lead.ico || "-"}</td>
                            <td style={s.td}>{lead.ownerName || "-"}</td>
                            <td style={s.td}>{lead.forecast ?? lead.FC ?? "-"}</td>
                            <td style={s.td}>{getBmsl(lead) > 0 ? getBmsl(lead).toLocaleString("cs-CZ") : "-"}</td>
                            <td style={s.td}>{lead.closedDate ? new Date(lead.closedDate).toLocaleDateString("cs-CZ") : "-"}</td>
                            <td style={s.td}>{lead.payoutStatus === "complete" ? "Proplaceno komplet" : lead.payoutStatus === "lead_only" ? "Proplaceno pouze lead" : "-"}</td>
                            <td style={s.td}>{isMarked(lead) ? (Math.round(getLeadInvoiceAmount(lead) * 100) / 100).toLocaleString("cs-CZ") : "-"} {isMarked(lead) ? "Kč" : ""}</td>
                            <td style={s.td}>{lead.payoutMarkedAt ? `${new Date(lead.payoutMarkedAt).toLocaleDateString("cs-CZ")} ${lead.payoutMarkedByName ? `(${lead.payoutMarkedByName})` : ""}` : "-"}</td>
                            <td style={s.td}>
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <button
                                  onClick={() => setLeadPayoutStatus(lead, "complete")}
                                  style={{
                                    ...s.btn,
                                    padding: "5px 8px",
                                    fontSize: 11,
                                    background: lead.payoutStatus === "complete" ? "#16a34a" : (isDarkMode ? "#1f2937" : "#f3f4f6"),
                                    color: lead.payoutStatus === "complete" ? "#fff" : text,
                                    border: `1px solid ${lead.payoutStatus === "complete" ? "#16a34a" : border}`,
                                  }}
                                >
                                  Proplaceno komplet
                                </button>
                                <button
                                  onClick={() => setLeadPayoutStatus(lead, "lead_only")}
                                  style={{
                                    ...s.btn,
                                    padding: "5px 8px",
                                    fontSize: 11,
                                    background: lead.payoutStatus === "lead_only" ? "#d97706" : (isDarkMode ? "#1f2937" : "#f3f4f6"),
                                    color: lead.payoutStatus === "lead_only" ? "#fff" : text,
                                    border: `1px solid ${lead.payoutStatus === "lead_only" ? "#d97706" : border}`,
                                  }}
                                >
                                  Proplaceno pouze lead
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
